<script setup>
import { computed, ref, onMounted } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import MemoryPopover from 'next/message/MemoryPopover.vue';
import AiTracePopover from './AiTracePopover.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useMapGetter } from 'dashboard/composables/store';
import {
  channelOfInbox,
  productHref as resolveProductHref,
  LISTING_URL_RE,
} from 'dashboard/helper/productLink';

const {
  content,
  attachments,
  contentAttributes,
  messageType,
  id: messageId,
  inboxId,
  sourceId,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) return content.value;
  if (hasTranslations.value) return translationContent.value;
  return content.value;
});

const isTemplate = computed(() => messageType.value === MESSAGE_TYPES.TEMPLATE);
const isEmpty = computed(() => !content.value && !attachments.value?.length);
const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};

const attrs = computed(
  () => contentAttributes?.value ?? contentAttributes ?? {}
);

// AI Draft detection
const isAiDraft = computed(
  () =>
    attrs.value.ai_draft === true ||
    attrs.value.aiDraft === true ||
    // 2026-06-30 (owner): agentic 💡 draft notes post EMPTY content_attributes (no ai_draft
    // flag), so also detect by the "💡 AI Draft" text prefix → every draft renders as the one
    // collapsible card with Send / Paste-to-Chat / Comment, never a buttonless plain bubble.
    /^\s*💡\s*AI Draft/.test(content?.value || '')
);

// Real AI sends (night concierge / countdown / pilot, 2026-06-12): outgoing
// messages keep [Product:] tokens for marketplace card expansion — render
// them as card chips here too so staff see what the customer sees.
const isAiSent = computed(
  () =>
    !isAiDraft.value &&
    messageType.value === MESSAGE_TYPES.OUTGOING &&
    // this render path CAMELIZES nested content_attributes keys (hence the
    // existing ai_draft || aiDraft double-checks) — test both shapes
    (attrs.value.ai_generated === true || attrs.value.aiGenerated === true)
);

const draftReplyText = computed(() => {
  const text = content?.value ?? '';
  // "AI Draft+" (optional +) = a day-safe-class draft that WOULD auto-send once the owner arms
  // day_safe_classes (mk-agent server-bridge.js agenticDraftNote, owner 2026-07-02).
  const match = text.match(/^💡 AI Draft\+? \([^)]+\)\n\n([\s\S]+)$/);
  return match ? match[1].trim() : '';
});

const draftHeader = computed(() => {
  const text = content?.value ?? '';
  const match = text.match(/^(💡 AI Draft\+? \([^)]+\))/);
  return match ? match[1] : '';
});

// Draft+ = would auto-send once day_safe_classes is armed. Rendered as "AI Draft" + a blue
// [PLUS] pill (owner 2026-07-02) instead of a bare "+" in the text.
const isDraftPlus = computed(() => /^💡 AI Draft\+/.test(content?.value ?? ''));
const draftHeaderDisplay = computed(() => draftHeader.value.replace('AI Draft+', 'AI Draft'));

// Phase B (.claude/plans/cs-ai-failsafe-and-needhuman.md): a held/deferred draft carries a
// structured ai_card { attention, reason, ... }. attention='needs_human' means a human must
// review/approve — surface the red "Needs Human" pill next to the draft header while the Send
// button keeps working. (2026-07-01, owner: the separate reason line was dropped as redundant
// with the pill.) This render path camelizes nested keys, so read both ai_card / aiCard.
const aiCard = computed(() => attrs.value.ai_card || attrs.value.aiCard || null);
// Owner 2026-07-02 — the per-turn ACTION TRACE (mk-agent ai_card.trace): the numbered step list
// (planner → tool → gate → decision → delivery). Rendered as a hoverable (i) beside the draft
// header AND on any other message carrying it (e.g. 🔴 escalation notes). Selectable text so
// staff can copy-paste it to an AI and ask why a step happened.
const aiTrace = computed(() => {
  const t = aiCard.value?.trace;
  return Array.isArray(t) && t.length ? t : [];
});
const needsHuman = computed(
  () =>
    aiCard.value?.attention === 'needs_human' ||
    attrs.value.needs_human === true ||
    attrs.value.needsHuman === true
);

const backendUrl = computed(
  () =>
    attrs.value.backend_url || attrs.value.backendUrl || 'https://sapi.mkhb.my'
);
const draftSendState = ref('idle');
// 1.2 (2026-06-10): historical drafts render collapsed — the AiDraftBanner
// above the reply box owns the LATEST draft; the feed keeps full history
// one click away without flooding the conversation.
// 2026-06-30 (owner): the above-composer AiDraftBanner is disabled, so the NEWEST 💡 draft in
// the conversation renders EXPANDED (prominent) while older drafts collapse by default to save
// space. The collapse/expand buttons still let staff toggle any card (collapseOverride wins).
const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');
const latestDraftId = computed(() => {
  const msgs = currentChat.value?.messages || [];
  let latest = null;
  for (const m of msgs) {
    const isDraft =
      /^\s*💡\s*AI Draft/.test(m.content || '') ||
      m.content_attributes?.ai_draft === true;
    if (isDraft && (!latest || (m.created_at || 0) >= (latest.created_at || 0))) {
      latest = m;
    }
  }
  return latest ? latest.id : null;
});
const isLatestDraft = computed(
  () => latestDraftId.value != null && latestDraftId.value === messageId.value
);
const collapseOverride = ref(null); // null = follow default; true|false = staff toggled
const draftCollapsed = computed({
  get: () =>
    collapseOverride.value !== null ? collapseOverride.value : !isLatestDraft.value,
  set: v => {
    collapseOverride.value = v;
  },
});

const accountId = computed(() => {
  const m = window.location.pathname.match(/accounts\/(\d+)/);
  return m ? m[1] : '';
});
const conversationId = computed(() => {
  const m = window.location.pathname.match(/conversations\/(\d+)/);
  return m ? m[1] : '';
});

// Universal [Product:<sku>] tag system — n8n emits tokens inline in draft text.
// We fetch card data from /api/products/cards at render time so the draft stays
// a single source of truth (the text).
const parseProductToken = raw => {
  const token = String(raw || '').trim();
  const parts = token
    .split(':')
    .map(part => part.trim())
    .filter(Boolean);
  const sku = parts.length >= 2 && /^\d+$/.test(parts[0]) ? parts[1] : parts[0];
  return { token, sku };
};

const draftProductTokens = computed(() => {
  const text = isAiSent.value
    ? (content?.value ?? '')
    : draftReplyText.value || '';
  const tokens = [];
  const re = /\[Product:([^\]]+)\]/g;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    const parsed = parseProductToken(m[1]);
    if (parsed.sku && !tokens.some(item => item.token === parsed.token)) {
      tokens.push(parsed);
    }
  }
  return tokens;
});

const draftSkus = computed(() => [
  ...new Set(draftProductTokens.value.map(item => item.sku)),
]);
const cardData = ref({});
const cardSkusExcluded = ref(new Set());

async function fetchCards(skus) {
  if (!skus.length) return;
  try {
    const url = `/api/v1/accounts/${accountId.value}/mh_ai_assist/product_cards?skus=${encodeURIComponent(skus.join(','))}`;
    const { data } = await window.axios.get(url);
    cardData.value = data.cards || {};
  } catch {
    /* non-critical — tag still shows as chip fallback */
  }
}

onMounted(() => {
  if (draftSkus.value.length) fetchCards(draftSkus.value);
});

// Draft text with [Product:<sku>] tokens stripped — for display
const draftDisplayText = computed(() => {
  const text = draftReplyText.value || '';
  return text
    .replace(/\[Product:[^\]]+\]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
});

const draftCards = computed(() =>
  draftProductTokens.value.map(({ token, sku }) => ({
    token,
    sku,
    included: !cardSkusExcluded.value.has(token),
    card: cardData.value[sku] || null,
  }))
);

const toggleCard = token => {
  const s = new Set(cardSkusExcluded.value);
  if (s.has(token)) s.delete(token);
  else s.add(token);
  cardSkusExcluded.value = s;
};

// Legacy support: products enriched in content_attributes (buyer-shared items)
const draftProducts = computed(() =>
  (attrs.value.products || []).filter(p => p.name)
);

// Resolve the best link a product card can offer. Cards arrive with varying
// key shapes (parsed 🔗 emoji → sourceUrl; content_attributes.products →
// url / shopee_url; backend cardLookup → shopee_url + tiktok_id + lazada_id).
// The link is CHANNEL-AWARE: a card in a TikTok thread opens the TikTok
// listing, not Shopee's (owner report 2026-07-25, conv 23567).
const inboxGetter = useMapGetter('inboxes/getInbox');
const conversationInbox = computed(() =>
  inboxGetter.value?.(inboxId?.value ?? inboxId)
);
const conversationChannel = computed(() =>
  channelOfInbox(conversationInbox.value)
);
const productHref = p =>
  resolveProductHref(p, conversationChannel.value, conversationInbox.value);

// MakerHub: a card's `name` is the catalogue short_name, which for a VARIANT row
// can be a bare label like "UNSOLDERED" (short_name == the variant). Showing that
// alone makes the chip read as a product literally called "UNSOLDERED" (bug seen
// on ASRPRO1, 2026-06-29). Prefer the real listing name in that case, and surface
// the variant separately as a badge so the agent can tell which variant it is.
const cardTitle = card => {
  if (!card) return '';
  const variant = card.variant_name;
  if (variant && card.name === variant) return card.full_name || card.name;
  return card.name || card.full_name;
};
const cardVariant = card => {
  if (!card || !card.variant_name) return '';
  return card.variant_name === cardTitle(card) ? '' : card.variant_name;
};

// Universal product cards — from content_attributes.products OR parsed from emoji format
const productCards = computed(() => {
  if (isAiDraft.value) return [];
  if (isAiSent.value)
    return draftCards.value.map(c => ({
      name: c.card ? cardTitle(c.card) : c.sku,
      price: c.card?.price,
      sku: c.sku,
      image: c.card?.thumbnail,
      // Carry every marketplace id through — productHref picks the one that
      // matches this conversation's channel. Pinning shopee_url here is what
      // made TikTok/Lazada cards open Shopee.
      shopee_url: c.card?.shopee_url,
      tiktok_id: c.card?.tiktok_id,
      lazada_id: c.card?.lazada_id,
    }));

  // 1. Structured products from content_attributes (from [Product:] tag system)
  const structured = (attrs.value.products || []).filter(p => p.name || p.sku);
  if (structured.length) return structured;

  // 2. Parse emoji product format from text content:
  //    🛒 Product Name\n💰 RMx.xx\n🏷️ SKU\n🔗 URL
  const text = content?.value ?? '';
  if (!text.includes('🛒')) return [];
  const parsed = [];
  // Split by 🛒 to handle multiple products in one message
  const blocks = text.split(/(?=🛒\s)/).filter(b => b.trim());
  for (const block of blocks) {
    const nameMatch = block.match(/🛒\s*\*{0,2}(.+?)\*{0,2}\s*\n/);
    const priceMatch =
      block.match(/💰\s*RM([\d.]+)/i) || block.match(/RM([\d.]+)/);
    const skuMatch = block.match(/🏷️\s*(\S+)/);
    // A buyer sharing a card from TikTok/Lazada inlines THAT marketplace's
    // URL — matching only shopee.com.my dropped it and sent the tap to a
    // Shopee search instead (conv 23567, 2026-07-25).
    const urlMatch = block.match(LISTING_URL_RE);
    const stockMatch =
      block.match(/📦\s*Stock:\s*(\d+)/i) || block.match(/(\d+)\s*available/);
    // A 🛒 block is a real product card ONLY when it carries a product field —
    // a price (💰 RM…), a SKU (🏷️), or a marketplace listing link. A bare 🛒
    // used as decoration (e.g. the "🛒 Shopee" channel header in the CS briefing)
    // must NOT render an empty "No img / Shopee / RM" card. (2026-06-30)
    if (nameMatch && (priceMatch || skuMatch || urlMatch)) {
      parsed.push({
        name: nameMatch[1].trim(),
        price: priceMatch?.[1] || '',
        sku: skuMatch?.[1] || '',
        image: '', // no image in text, attachment handles it
        stock: stockMatch?.[1] ? Number(stockMatch[1]) : null,
        // The listing the customer is actually looking at — wins over any
        // channel-derived link.
        sourceUrl: urlMatch?.[0] || '',
      });
    }
  }
  return parsed;
});
const hasProductCards = computed(() => productCards.value.length > 0);

// When we detect emoji product cards, strip the text so only the card renders
const strippedContent = computed(() => {
  if (isAiSent.value)
    return (renderContent.value || '')
      .replace(/\[Product:[^\]]+\]/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  if (!hasProductCards.value || (attrs.value.products || []).length > 0)
    return renderContent.value;
  // If parsed from emoji format, strip the emoji lines — card replaces them
  const text = renderContent.value || '';
  if (!text.includes('🛒')) return text;
  return text
    .replace(/🛒\s*\*{0,2}.+?\*{0,2}\s*\n?/g, '')
    .replace(/💰\s*RM[\d.]+[^\n]*\n?/gi, '')
    .replace(/🏷️\s*\S+\s*\n?/g, '')
    .replace(new RegExp(`🔗?\\s*${LISTING_URL_RE.source}\\s*\\n?`, 'g'), '')
    .replace(/📦\s*Stock:\s*\d+[^\n]*\n?/gi, '')
    .replace(/RM[\d.]+\s*·\s*\d+\s*available\s*\n?/g, '')
    .trim();
});

const pasteToChatBox = () => {
  const payload = draftDisplayText.value || draftReplyText.value;
  if (!payload) return;
  const editor = document.querySelector('.reply-box .ProseMirror');
  if (editor) {
    editor.focus();
    editor.innerHTML = `<p>${payload.replace(/\n/g, '</p><p>')}</p>`;
    editor.dispatchEvent(new Event('input', { bubbles: true }));
  }
};

const showCommentBox = ref(false);
const commentState = ref('idle');

const submitComment = async ({ note, scope }) => {
  if (!note || !note.trim()) return;
  commentState.value = 'saving';
  try {
    const body = {
      kind: 'correction',
      conversationId: conversationId.value,
      messageId: messageId?.value,
      inboxId: inboxId?.value,
      draftHeader: draftHeader.value,
      draftText: draftReplyText.value,
      comment: note.trim(),
      timestamp: new Date().toISOString(),
    };
    if (scope && scope !== 'auto') {
      body.scope = scope === 'all' ? ['all'] : [scope];
    }
    const resp = await fetch(backendUrl.value + '/api/ai-improvement/comment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    commentState.value = resp.ok ? 'saved' : 'error';
    if (resp.ok) {
      setTimeout(() => {
        showCommentBox.value = false;
        commentState.value = 'idle';
      }, 1500);
    }
  } catch {
    commentState.value = 'error';
  }
};

const sendDraft = async () => {
  if (!draftReplyText.value || draftSendState.value === 'sent') return;
  draftSendState.value = 'sending';
  // Strip excluded [Product:<sku>] tokens before sending. Included SKUs stay in
  // the text — outgoing processors (Shopee adapter, WA adapter) expand them.
  let text = draftReplyText.value;
  cardSkusExcluded.value.forEach(token => {
    const re = new RegExp(
      `\\[Product:${token.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\]`,
      'g'
    );
    text = text.replace(re, '');
  });
  // A3 (2026-06-30): strip the AI STATUS FOOTER ("— deferred to a human" / "— held for review" /
  // "— held — possible internal content") that mk-agent appends to the note body (server-bridge.js
  // STATUS_FOOTER). It is staff-only context and must NEVER reach the customer. Anchored to the END
  // so a legitimate mid-sentence "deferred"/"held" is untouched.
  text = text
    .replace(
      /\n+—\s*(deferred to a human|held for review|held — possible internal content)\s*$/i,
      ''
    )
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  try {
    const resp = await fetch(backendUrl.value + '/api/chat-triage/send-draft', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        conversationId: conversationId.value,
        accountId: accountId.value,
        messageId: messageId?.value,
        text,
        shopeeItemIds:
          attrs.value.shopee_item_ids || attrs.value.shopeeItemIds || [],
        products: attrs.value.products || [],
        sentBy: {
          id: currentUser.value?.id,
          name: currentUser.value?.name,
        },
      }),
    });
    draftSendState.value = resp.ok ? 'sent' : 'error';
  } catch {
    draftSendState.value = 'error';
  }
};

// Shopee direct reply detection
const isShopeeDirectReply = computed(
  () => attrs.value.shopee_direct === true || attrs.value.shopeeDirect === true
);

// WhatsApp "replied elsewhere" detection — sent from the phone/app (native WhatsApp
// id), not from omnichat (omnichat/API sends are relayed by Baileys as 'WAID:3EB0…').
// Gated to outgoing; incoming customer messages also carry non-3EB0 WAID ids.
const isWaDirectReply = computed(() => {
  if (messageType.value !== MESSAGE_TYPES.OUTGOING) return false;
  // Guard: when sourceId.value is nullish the `?? sourceId` fallback yields the
  // ref OBJECT (not a string), so .startsWith threw "is not a function" and
  // crashed the whole outgoing bubble render (blank AI/agent messages, 2026-06-16).
  const sid = sourceId?.value ?? sourceId;
  return (
    typeof sid === 'string' &&
    sid.startsWith('WAID:') &&
    !sid.startsWith('WAID:3EB0')
  );
});

// Only a plain text bubble (no product cards, attachments, AI draft, or channel
// override) gets the WhatsApp inline-time treatment — those richer layouts render
// their own blocks below the text, where an absolutely-positioned time would clash.
const isPlainText = computed(
  () =>
    !!strippedContent.value &&
    !hasProductCards.value &&
    !attachments.value?.length &&
    !isAiDraft.value &&
    !isShopeeDirectReply.value &&
    !isWaDirectReply.value
);
</script>

<template>
  <BaseBubble
    class="px-3 py-1.5"
    :class="{ 'wa-inline-meta': isPlainText }"
    data-bubble-name="text"
  >
    <div class="gap-3 flex flex-col">
      <!-- AI Draft layout (1.2: collapsed by default — banner shows the latest) -->
      <template v-if="isAiDraft && draftReplyText && draftCollapsed">
        <button
          class="flex w-full items-center gap-2 text-left text-xs opacity-70 hover:opacity-100"
          data-test-id="ai-draft-collapsed"
          @click="draftCollapsed = false"
        >
          <span class="shrink-0">{{ draftHeaderDisplay }}</span>
          <span
            v-if="isDraftPlus"
            class="shrink-0 rounded-full text-xxs font-semibold leading-4 px-1.5 py-0 text-white bg-n-blue-9"
            data-test-id="ai-draft-plus-pill"
            title="Would auto-send once daytime auto-send is armed"
            >PLUS</span>
          <span
            v-if="needsHuman"
            class="shrink-0 rounded-full text-xxs font-semibold leading-4 px-1.5 py-0 text-white bg-n-ruby-9"
            data-test-id="ai-card-pill"
            >Needs Human</span>
          <!-- ONE (i): the trace popover absorbs the needs-human reason when a trace exists
               (owner 2026-07-02: "there is two (i) button"); reason-only ⓘ kept for old notes. -->
          <span
            v-if="needsHuman && aiCard?.reason && !aiTrace.length"
            class="shrink-0 cursor-help opacity-70 hover:opacity-100"
            :title="aiCard.reason"
            data-test-id="ai-card-reason-info"
            >ⓘ</span>
          <AiTracePopover
            v-if="aiTrace.length"
            class="shrink-0"
            :steps="aiTrace"
            :reason="needsHuman ? aiCard?.reason : ''"
            @click.stop
          />
          <span class="truncate">{{ draftDisplayText }}</span>
          <span class="ml-auto shrink-0 underline">expand</span>
        </button>
      </template>
      <template v-else-if="isAiDraft && draftReplyText">
        <!-- Owner directive (2026-07-01): "💡 AI Draft (…)" label sits on the LEFT, the red
             "Needs Human" pill to its right — same badge as the conversation list
             (ConversationCard.vue, bg-n-ruby-9), no 🔴 emoji. The cardReason line
             ("deferred to a human") was removed here — it duplicated the pill. -->
        <div class="text-xs font-medium opacity-70 flex items-center gap-1.5">
          <span>{{ draftHeaderDisplay }}</span>
          <span
            v-if="isDraftPlus"
            class="rounded-full text-xxs font-semibold leading-4 px-1.5 py-0 text-white bg-n-blue-9"
            data-test-id="ai-draft-plus-pill"
            title="Would auto-send once daytime auto-send is armed"
            >PLUS</span>
          <span
            v-if="needsHuman"
            class="rounded-full text-xxs font-semibold leading-4 px-1.5 py-0 text-white bg-n-ruby-9"
            data-test-id="ai-card-pill"
            >Needs Human</span>
          <!-- ONE (i): the trace popover absorbs the needs-human reason when a trace exists. -->
          <span
            v-if="needsHuman && aiCard?.reason && !aiTrace.length"
            class="cursor-help opacity-70 hover:opacity-100"
            :title="aiCard.reason"
            data-test-id="ai-card-reason-info"
            >ⓘ</span>
          <AiTracePopover
            v-if="aiTrace.length"
            :steps="aiTrace"
            :reason="needsHuman ? aiCard?.reason : ''"
          />
          <button
            class="ml-auto text-[10px] underline opacity-60"
            @click="draftCollapsed = true"
          >
            collapse
          </button>
        </div>
        <div
          class="px-2.5 py-1.5 rounded-md border border-current/10 bg-n-alpha-2 whitespace-pre-wrap text-[13px] leading-snug"
        >
          {{ draftDisplayText }}
        </div>
        <!-- Product cards from [Product:<sku>] tags — one Send button sends text+tags together -->
        <div v-if="draftCards.length" class="flex flex-col gap-1.5 mt-1">
          <label
            v-for="c in draftCards"
            :key="c.token"
            class="flex gap-3 rounded-lg overflow-hidden border border-current/10 bg-n-alpha-1 cursor-pointer transition-opacity"
            :class="c.included ? '' : 'opacity-40'"
          >
            <input
              type="checkbox"
              :checked="c.included"
              class="sr-only"
              @change="toggleCard(c.token)"
            />
            <img
              v-if="c.card && c.card.thumbnail"
              :src="c.card.thumbnail"
              :alt="c.card.name"
              class="w-16 h-16 object-cover flex-shrink-0"
              loading="lazy"
            />
            <div
              v-else
              class="w-16 h-16 flex-shrink-0 bg-n-alpha-2 flex items-center justify-center text-n-slate-9 text-xs"
            >
              {{ c.sku }}
            </div>
            <div class="py-2 pr-3 flex flex-col justify-center min-w-0 flex-1">
              <div class="font-semibold text-xs line-clamp-2 leading-snug">
                {{ c.card ? cardTitle(c.card) : c.sku }}
              </div>
              <div class="flex items-center gap-2 mt-0.5 text-xs">
                <span
                  v-if="c.card && c.card.price"
                  class="text-n-ruby-11 font-semibold"
                  >RM{{ c.card.price }}</span>
                <span
                  v-if="c.card && cardVariant(c.card)"
                  class="text-[10px] px-1 rounded bg-n-alpha-2 text-n-slate-11"
                  >{{ cardVariant(c.card) }}</span>
                <span v-if="c.card && !c.card.in_stock"
class="text-n-slate-10"
                  >out of stock</span>
                <span v-else-if="c.card"
class="text-n-slate-10"
                  >{{ c.card.stock }} avail</span>
                <a
                  v-if="c.card"
                  :href="productHref(c.card)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="ml-auto text-[10px] px-1.5 py-0.5 rounded border border-current/20 opacity-70 hover:opacity-100 no-underline"
                  title="Open the listing in a new tab to verify"
                  @click.stop
                  >🔗 Open</a>
                <span class="text-[10px] opacity-70">{{
                  c.included ? '✓ include' : 'excluded'
                }}</span>
              </div>
            </div>
          </label>
        </div>
        <!-- Legacy buyer-shared product cards (content_attributes.products) -->
        <a
          v-for="(p, i) in draftProducts"
          :key="'legacy-' + i"
          :href="productHref(p)"
          target="_blank"
          rel="noopener noreferrer"
          class="flex gap-3 bg-n-alpha-2 rounded-lg overflow-hidden border border-current/10 no-underline mt-1"
          ><img
            v-if="p.image"
            :src="p.image"
            :alt="p.name"
            class="w-16 h-16 object-cover flex-shrink-0"
            loading="lazy"
          />
          <div class="py-2 pr-3 flex flex-col justify-center min-w-0">
            <div class="font-semibold text-xs line-clamp-2 leading-snug">
              {{ p.name }}
            </div>
            <div class="text-xs opacity-70 mt-0.5">RM{{ p.price }}</div>
          </div></a>
        <div class="flex items-center gap-2.5">
          <!-- Send: filled round button (blue), like the composer's own send -->
          <button
            v-if="draftSendState === 'idle'"
            class="inline-flex size-6 items-center justify-center rounded-full text-white cursor-pointer bg-[#1b73e8] hover:bg-[#1a68d1]"
            title="Send to Customer"
            @click="sendDraft"
          >
            <i class="i-ph-arrow-up size-4 shrink-0" />
          </button>
          <button
            v-else-if="draftSendState === 'sending'"
            class="inline-flex size-6 items-center justify-center rounded-full text-white cursor-default bg-[#6b7280]"
            title="Sending…"
            disabled
          >
            <i class="i-lucide-loader-circle size-4 shrink-0 animate-spin" />
          </button>
          <span
            v-else-if="draftSendState === 'sent'"
            class="inline-flex size-6 items-center justify-center rounded-full text-white bg-[#16a34a]"
            title="Sent to customer"
          >
            <i class="i-lucide-check size-4 shrink-0" />
          </span>
          <button
            v-else
            class="inline-flex size-6 items-center justify-center rounded-full text-white cursor-pointer bg-[#dc2626] hover:bg-[#c81e1e]"
            title="Failed — retry send"
            @click="sendDraft"
          >
            <i class="i-lucide-rotate-cw size-4 shrink-0" />
          </button>
          <!-- Paste & Comment: bare icons, inherit the card's text color -->
          <button
            v-if="draftSendState !== 'sent'"
            class="inline-flex items-center cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
            title="Paste to Chat"
            @click="pasteToChatBox"
          >
            <i class="i-lucide-clipboard size-4" />
          </button>
          <div class="relative inline-block">
            <button
              v-if="draftSendState !== 'sent'"
              class="inline-flex items-center cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
              title="Comment to AI"
              @click="showCommentBox = !showCommentBox"
            >
              <i class="i-lucide-message-circle size-4" />
            </button>
            <MemoryPopover
              v-model="showCommentBox"
              title="AI improvement comment"
              placeholder="What should the AI do differently? (e.g. wrong product, tone, missing info...)"
              anchor="right"
              :state="commentState"
              @submit="submitComment"
            />
          </div>
        </div>
      </template>
      <!-- Shopee direct reply -->
      <template v-else-if="isShopeeDirectReply">
        <div class="text-xs font-medium text-white/60">
          🛒 Replied on Shopee
        </div>
        <FormattedContent v-if="strippedContent" :content="strippedContent" />
        <div v-if="hasProductCards" class="flex flex-col gap-1.5 mt-1">
          <a
            v-for="(p, i) in productCards"
            :key="'sr-' + i"
            :href="productHref(p)"
            target="_blank"
            rel="noopener noreferrer"
            class="flex gap-3 rounded-lg overflow-hidden border border-white/10 bg-white/10 no-underline hover:bg-white/15 transition-colors"
          >
            <img
              v-if="p.image || (attachments && attachments[i])"
              :src="
                p.image ||
                attachments[i]?.dataUrl ||
                attachments[i]?.data_url ||
                ''
              "
              :alt="p.name"
              class="w-14 h-14 object-cover flex-shrink-0"
              loading="lazy"
            />
            <div class="py-2 pr-3 flex flex-col justify-center min-w-0">
              <div
                class="font-semibold text-xs leading-snug line-clamp-2 text-white"
              >
                {{ p.name || p.sku }}
              </div>
              <div v-if="p.price" class="text-xs text-white/70 mt-0.5">
                RM{{ p.price }}
              </div>
            </div>
          </a>
        </div>
        <AttachmentChips
          v-if="!hasProductCards"
          :attachments="attachments"
          class="gap-2"
        />
      </template>
      <!-- WhatsApp: replied from the phone/app (synced back via Evolution) -->
      <template v-else-if="isWaDirectReply">
        <div class="text-xs font-medium text-white/60">
          💬 Replied on WhatsApp
        </div>
        <FormattedContent v-if="strippedContent" :content="strippedContent" />
        <AttachmentChips :attachments="attachments" class="gap-2" />
      </template>
      <!-- Default text bubble -->
      <template v-else>
        <span v-if="isEmpty && !hasProductCards" class="text-n-slate-11">{{
          $t('CONVERSATION.NO_CONTENT')
        }}</span>
        <!-- Per-turn AI action trace on non-draft carriers (🔴 escalation / needs-human notes) -->
        <div
          v-if="aiTrace.length"
          class="flex items-center gap-1.5 text-xs opacity-70"
        >
          <AiTracePopover :steps="aiTrace" />
          <span>AI action trace</span>
        </div>
        <FormattedContent v-if="strippedContent" :content="strippedContent" />
        <!-- Universal product cards -->
        <div v-if="hasProductCards" class="flex flex-col gap-1.5 mt-1">
          <a
            v-for="(p, i) in productCards"
            :key="i"
            :href="productHref(p)"
            target="_blank"
            rel="noopener noreferrer"
            class="flex gap-3 rounded-lg overflow-hidden border border-n-weak bg-n-alpha-1 no-underline hover:bg-n-alpha-2 transition-colors"
          >
            <img
              v-if="p.image || (attachments && attachments[i])"
              :src="
                p.image ||
                attachments[i]?.dataUrl ||
                attachments[i]?.data_url ||
                ''
              "
              :alt="p.name"
              class="w-14 h-14 object-cover flex-shrink-0"
              loading="lazy"
            />
            <div
              v-else
              class="w-14 h-14 flex-shrink-0 bg-n-alpha-2 flex items-center justify-center text-n-slate-9 text-xs"
            >
              No img
            </div>
            <div class="py-2 pr-3 flex flex-col justify-center min-w-0">
              <div class="font-semibold text-xs leading-snug line-clamp-2">
                {{ p.name || p.sku }}
              </div>
              <div class="flex items-center gap-2 mt-0.5 text-xs">
                <span class="text-n-ruby-11 font-semibold">RM{{ p.price }}</span>
                <span v-if="p.stock"
class="text-n-slate-10"
                  >{{ p.stock }} available</span>
              </div>
            </div>
          </a>
        </div>
        <TranslationToggle
          v-if="hasTranslations"
          class="-mt-3"
          :showing-original="renderOriginal"
          @toggle="handleSeeOriginal"
        />
        <AttachmentChips
          v-if="!hasProductCards"
          :attachments="attachments"
          class="gap-2"
        />
        <template v-if="isTemplate">
          <div
            v-if="contentAttributes.submittedEmail"
            class="px-2 py-1 rounded-lg bg-n-alpha-3"
          >
            {{ contentAttributes.submittedEmail }}
          </div>
        </template>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
